import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useReadableOnPrimary } from '../hooks/useReadableOnPrimary';

/**
 * Long-form city/lifestyle overview — mirrors the "{City} Lifestyle Overview"
 * section on flatz520.com/p/lifestyle/. An intro paragraph introduces the
 * surrounding city, followed by six numbered subsections (each a title +
 * richtext body that supports lists and inline links), then a short closing
 * paragraph that leads into the final CTA.
 *
 * The six item titles / bodies are intentionally generic placeholders in the
 * schema — the real content is hyperlocal and property-specific, filled in by
 * the per-token `generator` at generation time (or by editors hand-authoring
 * per site).
 */

export function LifestyleCommunity() {
  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const itemsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });
  const readableOnPrimary = useReadableOnPrimary();

  return (
    <section style={readableOnPrimary} className="w-full py-14 md:py-20 bg-[var(--vibe-color-primary)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headingRef} className="reveal-fade-up text-center mb-8 md:mb-10">
          <RichTextContent
            path="lifestylecommunity.eyebrow"
            label="Section Eyebrow"
            defaultValue="<p>The Area</p>"
            as="p"
            className="font-body text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[var(--vibe-color-text-on-primary)] mb-3"
          />
          <RichTextContent
            path="lifestylecommunity.title"
            label="Section Title"
            defaultValue='<p><span data-type="field-token" data-field="property_address_city"></span> Lifestyle Overview</p>'
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text-on-primary)] leading-tight tracking-tight"
          />
          <div className="w-16 h-1 mx-auto mt-5 rounded-full bg-[var(--vibe-color-text-on-primary)] opacity-60" />
        </div>

        <RichTextContent
          path="lifestylecommunity.intro"
          label="Intro"
          defaultValue='<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span> offers a blend of lifestyles — from quiet neighborhoods to short-commute convenience to everything the region has to offer.</p>'
          as="div"
          className="font-body text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2 text-center max-w-3xl mx-auto mb-10 md:mb-12 [&_p]:mb-3 last:[&_p]:mb-0"
        />

        <div ref={itemsRef} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 lg:gap-x-14 gap-y-8 md:gap-y-10">
          {/* Subsection 1 */}
          <div data-reveal className="reveal-fade-up grid grid-cols-[auto_1fr] gap-4 md:gap-5">
            <span className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text-on-primary)] opacity-70 leading-none tracking-tight pt-0.5">01</span>
            <div>
              <RichTextContent path="lifestylecommunity.item1_title" defaultValue="<p>Lifestyle Highlight 1</p>" as="h3" className="font-heading text-lg sm:text-xl font-normal text-[var(--vibe-color-text-on-primary)] tracking-tight mb-2" />
              <RichTextContent path="lifestylecommunity.item1_body" defaultValue="<ul><li>Describe a defining aspect of life in this neighborhood or city</li><li>Add a concrete local detail — amenity, employer, park, school, etc.</li><li>Call out why residents choose this area</li></ul>" as="div" className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-2 last:[&_p]:mb-0" />
            </div>
          </div>

          {/* Subsection 2 */}
          <div data-reveal className="reveal-fade-up grid grid-cols-[auto_1fr] gap-4 md:gap-5">
            <span className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text-on-primary)] opacity-70 leading-none tracking-tight pt-0.5">02</span>
            <div>
              <RichTextContent path="lifestylecommunity.item2_title" defaultValue="<p>Lifestyle Highlight 2</p>" as="h3" className="font-heading text-lg sm:text-xl font-normal text-[var(--vibe-color-text-on-primary)] tracking-tight mb-2" />
              <RichTextContent path="lifestylecommunity.item2_body" defaultValue="<ul><li>Describe a different defining aspect than item 1</li><li>Add a concrete local detail specific to this area</li><li>Explain why a different resident type is drawn here</li></ul>" as="div" className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-2 last:[&_p]:mb-0" />
            </div>
          </div>

          {/* Subsection 3 */}
          <div data-reveal className="reveal-fade-up grid grid-cols-[auto_1fr] gap-4 md:gap-5">
            <span className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text-on-primary)] opacity-70 leading-none tracking-tight pt-0.5">03</span>
            <div>
              <RichTextContent path="lifestylecommunity.item3_title" defaultValue="<p>Lifestyle Highlight 3</p>" as="h3" className="font-heading text-lg sm:text-xl font-normal text-[var(--vibe-color-text-on-primary)] tracking-tight mb-2" />
              <RichTextContent path="lifestylecommunity.item3_body" defaultValue="<ul><li>Describe a third angle on living here — different from items 1 and 2</li><li>Add a concrete local detail</li><li>Explain the practical benefit for residents</li></ul>" as="div" className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-2 last:[&_p]:mb-0" />
            </div>
          </div>

          {/* Subsection 4 */}
          <div data-reveal className="reveal-fade-up grid grid-cols-[auto_1fr] gap-4 md:gap-5">
            <span className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text-on-primary)] opacity-70 leading-none tracking-tight pt-0.5">04</span>
            <div>
              <RichTextContent path="lifestylecommunity.item4_title" defaultValue="<p>Lifestyle Highlight 4</p>" as="h3" className="font-heading text-lg sm:text-xl font-normal text-[var(--vibe-color-text-on-primary)] tracking-tight mb-2" />
              <RichTextContent path="lifestylecommunity.item4_body" defaultValue="<ul><li>Describe a fourth distinctive lifestyle angle for this area</li><li>Add a concrete local detail or feature</li><li>Say what residents who care about this will appreciate</li></ul>" as="div" className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-2 last:[&_p]:mb-0" />
            </div>
          </div>

          {/* Subsection 5 */}
          <div data-reveal className="reveal-fade-up grid grid-cols-[auto_1fr] gap-4 md:gap-5">
            <span className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text-on-primary)] opacity-70 leading-none tracking-tight pt-0.5">05</span>
            <div>
              <RichTextContent path="lifestylecommunity.item5_title" defaultValue="<p>Lifestyle Highlight 5</p>" as="h3" className="font-heading text-lg sm:text-xl font-normal text-[var(--vibe-color-text-on-primary)] tracking-tight mb-2" />
              <RichTextContent path="lifestylecommunity.item5_body" defaultValue="<ul><li>Describe a fifth defining lifestyle theme</li><li>Add concrete, hyperlocal evidence</li><li>Explain the day-to-day benefit for a resident</li></ul>" as="div" className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-2 last:[&_p]:mb-0" />
            </div>
          </div>

          {/* Subsection 6 */}
          <div data-reveal className="reveal-fade-up grid grid-cols-[auto_1fr] gap-4 md:gap-5">
            <span className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text-on-primary)] opacity-70 leading-none tracking-tight pt-0.5">06</span>
            <div>
              <RichTextContent path="lifestylecommunity.item6_title" defaultValue="<p>Lifestyle Highlight 6</p>" as="h3" className="font-heading text-lg sm:text-xl font-normal text-[var(--vibe-color-text-on-primary)] tracking-tight mb-2" />
              <RichTextContent path="lifestylecommunity.item6_body" defaultValue="<ul><li>Describe the final defining lifestyle theme for this area</li><li>Add concrete, hyperlocal detail</li><li>Close with the practical appeal for a resident</li></ul>" as="div" className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-2 last:[&_p]:mb-0" />
            </div>
          </div>
        </div>

        <RichTextContent
          path="lifestylecommunity.closing"
          label="Closing"
          defaultValue='<p>Whether you&rsquo;re looking for peace and quiet, community engagement, or quick access to the city, <span data-type="field-token" data-field="property_address_city"></span> has a lifestyle to match.</p>'
          as="div"
          className="mt-10 md:mt-12 font-body text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_a]:!text-inherit [&_a]:!underline [&_a]:underline-offset-2 text-center max-w-3xl mx-auto [&_p]:mb-3 last:[&_p]:mb-0"
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleCommunity',
  displayName: 'Lifestyle Community',
  tokens: {
    'lifestylecommunity.eyebrow': {
      label: 'Section Eyebrow',
      type: 'richtext',
      defaultValue: '<p>The Area</p>',
    },
    'lifestylecommunity.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_address_city"></span> Lifestyle Overview</p>',
    },
    'lifestylecommunity.intro': {
      label: 'Intro Paragraph',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span> offers a blend of lifestyles — from quiet neighborhoods to short-commute convenience to everything the region has to offer.</p>',
      generator: {
        prompt:
          'A short, hyperlocal intro paragraph for this property’s city/area that previews the six lifestyle themes below. Specific to this place — not generic praise. Mention the actual mix of resident types or living styles that this city is known for. Use Canadian spelling (e.g. "neighbourhood") for properties in Canada.',
        context:
          'Opens the city/lifestyle overview section on the Lifestyle page. Sits above six numbered subsections and above the closing paragraph.',
        length: '1–2 sentences',
      },
    },
    'lifestylecommunity.item1_title': {
      label: 'Item 1 Title',
      type: 'richtext',
      defaultValue: '<p>Lifestyle Highlight 1</p>',
      generator: {
        prompt:
          'A short headline (3–6 words) naming one of the strongest lifestyle themes or resident types in this specific city/area. Whatever is actually distinctive here — do not guess a type.',
        context:
          'Subsection 1 of 6 in the city/lifestyle overview. Different theme from siblings. Pick what’s most defining for this specific place.',
      },
    },
    'lifestylecommunity.item1_body': {
      label: 'Item 1 Body',
      type: 'richtext',
      defaultValue:
        '<ul><li>Describe a defining aspect of life in this neighborhood or city</li><li>Add a concrete local detail — amenity, employer, park, school, etc.</li><li>Call out why residents choose this area</li></ul>',
      generator: {
        prompt:
          'Three bullet points of hyperlocal, concrete detail supporting this subsection’s headline — real neighborhoods, parks, schools, employers, trails, cultural landmarks, etc. for this specific city. Use Canadian spelling (e.g. "neighbourhood") for properties in Canada. No generic praise or filler.',
        context:
          'Bullet list under subsection 1 of 6 in the city/lifestyle overview. Must match the item 1 headline and not overlap with siblings.',
      },
    },
    'lifestylecommunity.item2_title': {
      label: 'Item 2 Title',
      type: 'richtext',
      defaultValue: '<p>Lifestyle Highlight 2</p>',
      generator: {
        prompt:
          'A short headline (3–6 words) for a different strong lifestyle theme or resident type than item 1. Pick what’s actually distinctive about this specific city.',
        context:
          'Subsection 2 of 6 in the city/lifestyle overview. Must not overlap with item 1 or items 3–6.',
      },
    },
    'lifestylecommunity.item2_body': {
      label: 'Item 2 Body',
      type: 'richtext',
      defaultValue:
        '<ul><li>Describe a different defining aspect than item 1</li><li>Add a concrete local detail specific to this area</li><li>Explain why a different resident type is drawn here</li></ul>',
      generator: {
        prompt:
          'Three bullet points of hyperlocal detail supporting this subsection’s headline. Specific places, programs, or features in this city. Use Canadian spelling (e.g. "neighbourhood") for properties in Canada. No generic praise.',
        context:
          'Bullet list under subsection 2 of 6 in the city/lifestyle overview. Must match item 2 headline and not overlap with siblings.',
      },
    },
    'lifestylecommunity.item3_title': {
      label: 'Item 3 Title',
      type: 'richtext',
      defaultValue: '<p>Lifestyle Highlight 3</p>',
      generator: {
        prompt:
          'A short headline (3–6 words) for a third distinctive lifestyle theme in this specific city/area. Different from items 1–2.',
        context:
          'Subsection 3 of 6 in the city/lifestyle overview. Must not overlap with siblings.',
      },
    },
    'lifestylecommunity.item3_body': {
      label: 'Item 3 Body',
      type: 'richtext',
      defaultValue:
        '<ul><li>Describe a third angle on living here — different from items 1 and 2</li><li>Add a concrete local detail</li><li>Explain the practical benefit for residents</li></ul>',
      generator: {
        prompt:
          'Three bullet points of hyperlocal detail supporting this subsection’s headline. Concrete, specific to this city. Use Canadian spelling (e.g. "neighbourhood") for properties in Canada. No generic praise.',
        context:
          'Bullet list under subsection 3 of 6 in the city/lifestyle overview. Must match item 3 headline and not overlap with siblings.',
      },
    },
    'lifestylecommunity.item4_title': {
      label: 'Item 4 Title',
      type: 'richtext',
      defaultValue: '<p>Lifestyle Highlight 4</p>',
      generator: {
        prompt:
          'A short headline (3–6 words) for a fourth distinctive lifestyle theme in this specific city/area. Different from items 1–3.',
        context:
          'Subsection 4 of 6 in the city/lifestyle overview. Must not overlap with siblings.',
      },
    },
    'lifestylecommunity.item4_body': {
      label: 'Item 4 Body',
      type: 'richtext',
      defaultValue:
        '<ul><li>Describe a fourth distinctive lifestyle angle for this area</li><li>Add a concrete local detail or feature</li><li>Say what residents who care about this will appreciate</li></ul>',
      generator: {
        prompt:
          'Three bullet points of hyperlocal detail supporting this subsection’s headline. Real places, features, or benefits in this city. Use Canadian spelling (e.g. "neighbourhood") for properties in Canada. No generic praise.',
        context:
          'Bullet list under subsection 4 of 6 in the city/lifestyle overview. Must match item 4 headline and not overlap with siblings.',
      },
    },
    'lifestylecommunity.item5_title': {
      label: 'Item 5 Title',
      type: 'richtext',
      defaultValue: '<p>Lifestyle Highlight 5</p>',
      generator: {
        prompt:
          'A short headline (3–6 words) for a fifth distinctive lifestyle theme in this specific city/area. Different from items 1–4.',
        context:
          'Subsection 5 of 6 in the city/lifestyle overview. Must not overlap with siblings.',
      },
    },
    'lifestylecommunity.item5_body': {
      label: 'Item 5 Body',
      type: 'richtext',
      defaultValue:
        '<ul><li>Describe a fifth defining lifestyle theme</li><li>Add concrete, hyperlocal evidence</li><li>Explain the day-to-day benefit for a resident</li></ul>',
      generator: {
        prompt:
          'Three bullet points of hyperlocal detail supporting this subsection’s headline. Specific, concrete evidence from this city. Use Canadian spelling (e.g. "neighbourhood") for properties in Canada. No generic praise.',
        context:
          'Bullet list under subsection 5 of 6 in the city/lifestyle overview. Must match item 5 headline and not overlap with siblings.',
      },
    },
    'lifestylecommunity.item6_title': {
      label: 'Item 6 Title',
      type: 'richtext',
      defaultValue: '<p>Lifestyle Highlight 6</p>',
      generator: {
        prompt:
          'A short headline (3–6 words) for a sixth distinctive lifestyle theme in this specific city/area. Different from items 1–5.',
        context:
          'Subsection 6 of 6 in the city/lifestyle overview. Must not overlap with siblings.',
      },
    },
    'lifestylecommunity.item6_body': {
      label: 'Item 6 Body',
      type: 'richtext',
      defaultValue:
        '<ul><li>Describe the final defining lifestyle theme for this area</li><li>Add concrete, hyperlocal detail</li><li>Close with the practical appeal for a resident</li></ul>',
      generator: {
        prompt:
          'Three bullet points of hyperlocal detail supporting this subsection’s headline. Concrete, specific to this city. Use Canadian spelling (e.g. "neighbourhood") for properties in Canada. No generic praise.',
        context:
          'Bullet list under subsection 6 of 6 in the city/lifestyle overview. Must match item 6 headline and not overlap with siblings.',
      },
    },
    'lifestylecommunity.closing': {
      label: 'Closing Paragraph',
      type: 'richtext',
      defaultValue:
        '<p>Whether you&rsquo;re looking for peace and quiet, community engagement, or quick access to the city, <span data-type="field-token" data-field="property_address_city"></span> has a lifestyle to match.</p>',
    },
  },
};

(LifestyleCommunity as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LifestyleCommunity;
